/**
 * LexikonCard Component
 * Individual term card with level badge, pillar indicator, and MCG badge
 */

import React, { useMemo } from "react";
import type { Language, LexiconItem } from "../types";
import { LEVEL_CONFIG, PILLAR_CONFIG } from "../types";

interface LexikonCardProps {
  item: LexiconItem;
  language: Language;
  onClick: (item: LexiconItem) => void;
  isCompact?: boolean;
  searchQuery?: string;
}

const LexikonCard: React.FC<LexikonCardProps> = ({
  item,
  language,
  onClick,
  isCompact = false,
  searchQuery = "",
}) => {
  const content = item[language];
  const levelConfig = LEVEL_CONFIG[item.level];
  const pillarConfig = PILLAR_CONFIG[item.pillar];
  const hasMiniCourses = item.miniCourses && item.miniCourses.length > 0;

  // Highlight search terms in text
  const highlightText = useMemo(() => {
    if (!searchQuery.trim()) return content.shortDefinition;

    const regex = new RegExp(`(${searchQuery.trim()})`, "gi");
    const parts = content.shortDefinition.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="text-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  }, [content.shortDefinition, searchQuery]);

  const handleClick = () => {
    onClick(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(item);
    }
  };

  if (isCompact) {
    return (
      <div
        className={`lexikon-card compact ${item.isFeatured ? "featured" : ""}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={content.term}
      >
        <div className="card-header">
          <span className="card-title">{content.term}</span>
          <div className="card-badges">
            <span
              className={`level-badge ${item.level.toLowerCase()}`}
              title={levelConfig.name[language]}
            >
              {levelConfig.icon}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article
      className={`lexikon-card ${item.isFeatured ? "featured" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${content.term} - ${content.shortDefinition}`}
    >
      {/* Header */}
      <div className="card-header">
        <h3 className="card-title">{content.term}</h3>
        <div className="card-badges">
          <span
            className={`pillar-badge pillar-${item.pillar.toLowerCase()}`}
            title={pillarConfig.name[language]}
          >
            {item.pillar}
          </span>
          <span
            className={`level-badge ${item.level.toLowerCase()}`}
            title={levelConfig.name[language]}
          >
            {levelConfig.icon} {item.level}
          </span>
        </div>
      </div>

      {/* Definition */}
      <p className="card-definition">{highlightText}</p>

      {/* Footer */}
      <div className="card-footer">
        <span className="card-category">
          📁 {item.category}
        </span>
        {hasMiniCourses && (
          <span className="card-mcg-badge">
            🎓 {item.miniCourses!.length} {language === "cs" ? "kurzy" : "courses"}
          </span>
        )}
      </div>
    </article>
  );
};

export default LexikonCard;
